import { currentUser } from "@app/routes/auth/selectors";
import { ROLES } from "@app/constants";
import {
  setAndSendSpellingToWS, getBoardStateById, updateStudentWord, setIsWaiting,
  setCodingWordColor, setCodingLetterColor, setCodingLetterCode, setSketch,
  setIsSubmit, setIsListen, setIsEcho, setIsWrite, setIsCoding, setIsSpell,
  setMistakeData, addMistakeData, deleteMistakeData, updateMistakeDataConcept,
  resetCoding, updateStudentSpellingStatus,
} from "@app/routes/activities/spelling/store/spellingSlice";
import { sendToMeasurementsWithParams } from "@app/routes/vocabulary/api/index";
import { MEASUREMENT_TYPES } from "@amplio-common/measurements/constants";
import { STUDENT_PROFILE_BOARD_ID } from "@app/routes/activities/spelling/constants";

const sendMeasurement = async (store, boardId, state, spellingState, params) => {
  store.dispatch(updateStudentSpellingStatus({ id: boardId, changes: { isUpdatingMeasurements: true, isUpdatingMeasurementsError: false } }));
  let response;
  try {
    response = await sendToMeasurementsWithParams(params);
    store.dispatch(updateStudentSpellingStatus({ id: boardId, changes: { isUpdatingMeasurements: false, isUpdatingMeasurementsError: false } }));
  } catch (e) {
    store.dispatch(updateStudentSpellingStatus({ id: boardId, changes: { isUpdatingMeasurements: false, isUpdatingMeasurementsError: true } }));
  }
  return response;
};

const sendMistakeData = (store, boardId, state, spellingState) => {
  const wordState = spellingState?.wordsState[spellingState?.therapistWordId];
  const task = { uuid: spellingState?.measurementUUID };
  
  const { word: target, baseWord, mistakeData } = wordState || {};

  const measurements = [{
    mistakes: mistakeData.mistake_data,
    client_id: `${spellingState.measurementUUID}_${spellingState?.therapistWordId}`,
    manual: 1,
    target,
    base_word: baseWord,
  }];
  sendMeasurement(store, boardId, state, spellingState, {
    measurement_type: MEASUREMENT_TYPES.SPELLING,
    task,
    measurements,
  });
};

const sendCodingData = (store, boardId, state, spellingState) => {
  const wordState = spellingState?.wordsState[spellingState?.currentWordId];
  const task = { uuid: spellingState?.measurementUUID };
  const { word: target, baseWord } = wordState || {};

  const letters = wordState?.coding?.letters && Object.keys(wordState?.coding?.letters).map(letterId => {
    const letter = wordState?.coding?.letters?.[letterId];
    const codes = letter?.codes && Object.values(letter.codes).map(code => ({ id: code?.value, name: code?.name }));
    return {
      letter_id: +letterId,
      color: letter?.color?.value,
      codes,
    };
  });
  const measurement = {
    client_id: `${spellingState.measurementUUID}_${spellingState?.currentWordId}`,
    manual: 1,
    coding: {
      is_esperanza: spellingState?.isEsperanza,
      color: wordState?.coding?.color?.value,
      sketch: wordState?.coding?.sketch,
      letters,
    },
    target,
    base_word: baseWord,
  };
  sendMeasurement(store, boardId, state, spellingState, {
    measurement_type: MEASUREMENT_TYPES.SPELLING,
    task,
    measurements: [measurement],
  });
};

const sendBoolFiled = (store, boardId, state, spellingState, title, filedName) => {
  const wordState = spellingState?.wordsState[spellingState?.currentWordId];
  const value = wordState?.[filedName];
  const { word: target, baseWord } = wordState || {};

  const task = { uuid: spellingState?.measurementUUID };

  const measurement = {
    client_id: `${spellingState.measurementUUID}_${spellingState?.currentWordId}`,
    manual: 1,
    [title]: value,
    task_stage: spellingState?.currentWordId,
    target,
    base_word: baseWord,
  };
  sendMeasurement(store, boardId, state, spellingState, {
    measurement_type: MEASUREMENT_TYPES.SPELLING,
    instant_create: true,
    task,
    measurements: [measurement],
  });
};

const studentBeforeActions = {
  //[setSelectedGame]: (store, boardId, state) => { },
};

const therapistBeforeActions = {
  //[setEndRecording]: (store, state, readingState) => { },
};

const studentAfterActions = {
  [setIsSubmit]: (store, boardId, state, spellingState) => {
    sendBoolFiled(store, boardId, state, spellingState, "is_submit", "isSubmit");
  },
  [setIsListen]: (store, boardId, state, spellingState) => {
    sendBoolFiled(store, boardId, state, spellingState, "is_look_and_listen", "setIsListen");
  },
  [setIsEcho]: (store, boardId, state, spellingState) => {
    sendBoolFiled(store, boardId, state, spellingState, "is_echo", "isEcho");
  },
  [setIsSpell]: (store, boardId, state, spellingState) => {
    sendBoolFiled(store, boardId, state, spellingState, "is_spell", "isSpell");
  },
  [setIsWrite]: (store, boardId, state, spellingState) => {
    sendBoolFiled(store, boardId, state, spellingState, "is_write", "isWrite");
  },
  [setIsCoding]: (store, boardId, state, spellingState) => {
    sendBoolFiled(store, boardId, state, spellingState, "is_coding", "isCoding");
  },

  [updateStudentWord]: async (store, boardId, state, spellingState) => {
    const wordState = spellingState?.wordsState[spellingState?.currentWordId];
    const written = wordState?.studentWord;
    const target = wordState?.word;

    const task = { uuid: spellingState?.measurementUUID };
    const measurement = {
      client_id: `${spellingState?.measurementUUID}_${spellingState?.currentWordId}`,
      written,
      manual: 0,
      target,
      task_stage: spellingState?.currentWordId,
    };
    const { response } = await sendMeasurement(store, boardId, state, spellingState, {
      measurement_type: MEASUREMENT_TYPES.SPELLING,
      instant_create: true,
      task,
      measurements: [measurement],
    });
    if (response?.measurements?.[0]) {
      const { mistake_data = [] } = response?.measurements?.[0];
      mistake_data && store.dispatch(setAndSendSpellingToWS({ reducer: setMistakeData,
        data: {
          boardId,
          value: { mistake_data, written, target, wordId: spellingState?.currentWordId },
        } }));
      sendCodingData(store, boardId, state, spellingState);
    }
    store.dispatch(setIsWaiting({ boardId, value: false }));
    sendBoolFiled(store, boardId, state, spellingState, "is_write", "isWrite", "isWriteAuto");
  },

  [setCodingWordColor]: (store, boardId, state, spellingState) => {
    sendCodingData(store, boardId, state, spellingState);
  },
  [setCodingLetterColor]: (store, boardId, state, spellingState) => {
    sendCodingData(store, boardId, state, spellingState);
  },
  [setSketch]: (store, boardId, state, spellingState) => {
    sendCodingData(store, boardId, state, spellingState);
  },
  [resetCoding]: (store, boardId, state, spellingState) => {
    sendCodingData(store, boardId, state, spellingState);
  },
  [setCodingLetterCode]: (store, boardId, state, spellingState) => {
    sendCodingData(store, boardId, state, spellingState);
    sendBoolFiled(store, boardId, state, spellingState, "is_coding", "isCoding", "isCodingAuto");
  },
};

const therapistAfterActions = {
  [addMistakeData]: (store, boardId, state, spellingState) => {
    sendMistakeData(store, boardId, state, spellingState);
  },
  [deleteMistakeData]: (store, boardId, state, spellingState) => {
    sendMistakeData(store, boardId, state, spellingState);
  },
  [updateMistakeDataConcept]: (store, boardId, state, spellingState) => {
    sendMistakeData(store, boardId, state, spellingState);
  },
};

export const handleSpellingBeforeAction = (store, stateBefore, action) => {
  const currUser = currentUser(stateBefore);
  const boardId = action?.payload?.boardId;

  if (!boardId || boardId <= 0) {
    return;
  }

  const spellingState = boardId && stateBefore?.spelling && getBoardStateById(boardId, stateBefore.spelling);
  
  spellingState && currUser.role === ROLES.STUDENT.id && studentBeforeActions[action.type]?.(store, boardId, stateBefore, spellingState, action?.payload?.value);
  spellingState && currUser.role === ROLES.THERAPIST.id && therapistBeforeActions[action.type]?.(store, boardId, stateBefore, spellingState, action?.payload?.value);
};

export const handleSpellingAfterAction = (store, state, action) => {
  const currUser = currentUser(state);
  const boardId = action?.payload?.boardId;
  
  if (!boardId || (boardId !== STUDENT_PROFILE_BOARD_ID && boardId <= 0)) {
    return;
  }
  
  const spellingState = boardId && state?.spelling && getBoardStateById(boardId, state.spelling);

  const isStudent = [ROLES.STUDENT.id, ROLES.CLINIC_ADMINISTRATOR.id].includes(currUser.role);

  spellingState && isStudent && studentAfterActions[action.type]?.(store, boardId, state, spellingState, action?.payload?.value);
  spellingState && currUser.role === ROLES.THERAPIST.id && therapistAfterActions[action.type]?.(store, boardId, state, spellingState, action?.payload?.value);
};
